import { CheckIcon, ChevronDownIcon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useState, type FC } from "react";
import Text from "../Text";
import { useTranslation } from "react-i18next";
import { QueryFilterField } from "@/API";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";

export const PROMOTIONS_TYPE = [
  QueryFilterField.BUY_X_GET_Y_FREE_PROMOTION_COUNT,
  QueryFilterField.ITEM_PERCENT_OFF_PROMOTION_COUNT,
  QueryFilterField.MENU_PERCENT_OFF_PROMOTION_COUNT,
  QueryFilterField.FREE_DELIVERY_PROMOTION_COUNT,
  QueryFilterField.FREE_ITEM_PROMOTION_COUNT,
];

type Props = {
  onChange?: (value: string[]) => void;
  defaultValue?: string[];
  isRestricted?: boolean;
};

const SelectPromotionsType: FC<Props> = ({
  onChange,
  defaultValue,
  isRestricted,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string[]>(defaultValue ?? []);

  useEffect(() => {
    onChange?.(value);
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryFilled"
          role="combobox"
          aria-expanded={open}
          className="w-[100%] justify-between bg-[white]"
          style={{
            border: "1px solid #B3B9C4",
          }}
          onClick={() => {
            if (isRestricted) {
              setActionTriggerNeedCore(true);
            }
          }}
        >
          <Text className="truncate">
            {value.length > 0
              ? value.map((v) => t(`filter_${v}`)).join(", ")
              : t("select_promotions_type")}
          </Text>

          {isRestricted ? (
            <Lock
              width={24}
              height={24}
              color="#535862"
              className="ml-2 h-4 w-4 shrink-0"
            />
          ) : (
            <ChevronDownIcon
              width={24}
              height={24}
              color="#758195"
              className="ml-2 h-4 w-4 shrink-0"
              style={{
                transition: "transform 0.3s",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[355px] p-[12px]">
        <div className="flex flex-col gap-[8px] my-[8px]">
          {PROMOTIONS_TYPE.map((promotionsType, index) => (
            <button
              key={`value-${promotionsType}-${index}`}
              onClick={() => {
                if (value.find((v) => v === promotionsType)) {
                  setValue((prevState) => {
                    const copyPrevState = [...prevState];
                    const promotionsTypeIndexToRemove =
                      copyPrevState.indexOf(promotionsType);

                    if (promotionsTypeIndexToRemove !== -1)
                      copyPrevState.splice(promotionsTypeIndexToRemove, 1);

                    return copyPrevState;
                  });
                } else {
                  setValue((prevState) => [...prevState, promotionsType]);
                }
              }}
              className="flex items-center gap-[8px] px-[12px] py-[8px] hover:bg-[#F1F2F4]"
            >
              {value.find((v) => v === promotionsType) && (
                <CheckIcon height={16} width={16} />
              )}

              <Text fontSize={16} fontWeight={400}>
                {t(`filter_${promotionsType}`)}
              </Text>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SelectPromotionsType;
